import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';

interface Invoice {
  id: number;
  invoice_number: string;
  customer_id: number;
  customer_name: string;
  customer_email?: string;
  invoice_date: string;
  due_date: string;
  status: string;
  subtotal: number;
  vat_amount: number;
  total_amount: number;
  paid_amount: number;
  outstanding_amount: number;
  subtotal_formatted: string;
  vat_amount_formatted: string;
  total_amount_formatted: string;
  paid_amount_formatted: string;
  outstanding_amount_formatted: string;
  line_items: Array<{
    id?: number;
    description: string;
    quantity: number;
    unit_price: number;
    vat_rate: string;
    vat_amount: number;
    line_total: number;
    vat_amount_formatted?: string;
    line_total_formatted?: string;
  }>;
  days_overdue?: number;
  is_overdue: boolean;
  payment_terms: number;
  notes?: string;
}

interface ViewInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  invoice: Invoice | null;
}

const ViewInvoiceDialog: React.FC<ViewInvoiceDialogProps> = ({
  open,
  onClose,
  invoice
}) => {
  if (!invoice) return null;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft': return 'default';
      case 'sent': return 'info';
      case 'paid': return 'success';
      case 'overdue': return 'error';
      case 'cancelled': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft': return '📝';
      case 'sent': return '📤';
      case 'paid': return '✅';
      case 'overdue': return '⚠️';
      case 'cancelled': return '❌';
      default: return '📄';
    }
  };

  const formatVATRate = (vatRate: string) => {
    switch (vatRate) {
      case 'STANDARD': return '20%';
      case 'REDUCED': return '5%';
      case 'ZERO': return '0%';
      case 'EXEMPT': return 'Exempt';
      default: return vatRate;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ViewIcon />
            Invoice Details - {invoice.invoice_number}
          </Box>
          <Chip
            label={`${getStatusIcon(invoice.status)} ${invoice.status.toUpperCase()}`}
            color={getStatusColor(invoice.status) as any}
            size="small"
          />
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Invoice Header Information */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {invoice.customer_name}
                </Typography>
                {invoice.customer_email && (
                  <Typography variant="body2" color="textSecondary">
                    {invoice.customer_email}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Invoice Information
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Invoice Date:</Typography>
                  <Typography variant="body2">
                    {new Date(invoice.invoice_date).toLocaleDateString('en-GB')}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Due Date:</Typography>
                  <Typography variant="body2">
                    {new Date(invoice.due_date).toLocaleDateString('en-GB')}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Payment Terms:</Typography>
                  <Typography variant="body2">{invoice.payment_terms} days</Typography>
                </Box>
                {invoice.is_overdue && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="error">Days Overdue:</Typography>
                    <Typography variant="body2" color="error" fontWeight="medium">
                      {invoice.days_overdue} days
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Line Items */}
        <Typography variant="h6" gutterBottom>
          Line Items
        </Typography>
        
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell align="center">Quantity</TableCell>
                <TableCell align="right">Unit Price</TableCell>
                <TableCell align="center">VAT Rate</TableCell>
                <TableCell align="right">VAT Amount</TableCell>
                <TableCell align="right">Line Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.line_items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="body2">{item.description}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{item.quantity}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">£{item.unit_price.toFixed(2)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{formatVATRate(item.vat_rate)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {item.vat_amount_formatted || `£${item.vat_amount.toFixed(2)}`}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {item.line_total_formatted || `£${item.line_total.toFixed(2)}`}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Totals */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Box sx={{ minWidth: 300 }}>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography>Subtotal:</Typography>
              <Typography>{invoice.subtotal_formatted}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography>VAT:</Typography>
              <Typography>{invoice.vat_amount_formatted}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Total Amount:</Typography>
              <Typography variant="h6">{invoice.total_amount_formatted}</Typography>
            </Box>
            
            {invoice.paid_amount > 0 && (
              <>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography color="success.main">Paid Amount:</Typography>
                  <Typography color="success.main">{invoice.paid_amount_formatted}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color={invoice.outstanding_amount > 0 ? "error.main" : "success.main"}>
                    Outstanding:
                  </Typography>
                  <Typography 
                    color={invoice.outstanding_amount > 0 ? "error.main" : "success.main"}
                    fontWeight="medium"
                  >
                    {invoice.outstanding_amount_formatted}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Notes */}
        {invoice.notes && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Notes
            </Typography>
            <Card>
              <CardContent>
                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                  {invoice.notes}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Payment Status */}
        <Card sx={{ bgcolor: invoice.status === 'paid' ? 'success.light' : invoice.is_overdue ? 'error.light' : 'info.light' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Payment Status
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body1">
                  Status: <strong>{invoice.status.toUpperCase()}</strong>
                </Typography>
                {invoice.is_overdue && (
                  <Typography variant="body2" color="error">
                    This invoice is {invoice.days_overdue} days overdue
                  </Typography>
                )}
              </Box>
              <Box textAlign="right">
                <Typography variant="h6" color={invoice.outstanding_amount > 0 ? "error.main" : "success.main"}>
                  {invoice.outstanding_amount > 0 ? 'Amount Due' : 'Paid in Full'}
                </Typography>
                <Typography variant="h5" fontWeight="bold" color={invoice.outstanding_amount > 0 ? "error.main" : "success.main"}>
                  {invoice.outstanding_amount_formatted}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button startIcon={<PrintIcon />} variant="outlined">
          Print
        </Button>
        <Button startIcon={<DownloadIcon />} variant="outlined">
          Download PDF
        </Button>
        {invoice.status !== 'paid' && (
          <>
            <Button startIcon={<SendIcon />} variant="outlined" color="info">
              Send to Customer
            </Button>
            <Button startIcon={<PaymentIcon />} variant="contained" color="success">
              Record Payment
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ViewInvoiceDialog;
